import React from "react";

interface ContainerDimensions {
  width: number;
  height: number;
}

/**
 * Custom hook that tracks the dimensions of a container element.
 *
 * @example
 * ```tsx
 * const container = useContainerDimensions()
 *
 * return <div ref={container.ref}> {container.dimensions.width} </div>
 * ```
 */
export const useContainerDimensions = (): {
  setContainerRef: (ref: HTMLDivElement | null) => void;
  dimensions: ContainerDimensions;
} => {
  // Why don't we use `React.useRef` here?
  // Because we trigger a re-render after the ref changes,
  // otherwise the initial container dimensions would be zero.
  //
  // See https://stackoverflow.com/a/59605067/6587634
  const [containerRef, setContainerRef] =
    React.useState<HTMLDivElement | null>();

  const [dimensions, setDimensions] = React.useState<ContainerDimensions>({
    width: 0,
    height: 0,
  });

  React.useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef) {
        return;
      }
      if (containerRef) {
        setDimensions({
          width: containerRef.offsetWidth,
          height: containerRef.offsetHeight,
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Listen for window resize events
    window.addEventListener("resize", updateDimensions);

    // Use ResizeObserver for more precise tracking if available
    let resizeObserver: ResizeObserver | undefined;
    if (window.ResizeObserver && containerRef) {
      resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(containerRef);
    }

    return () => {
      window.removeEventListener("resize", updateDimensions);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [containerRef]);

  return {
    setContainerRef: (ref) => setContainerRef(ref),
    dimensions,
  };
};
